"use client"

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
// getToken を追加でインポートします
import { getAuthHeader, getToken } from '../utils/auth';
import {
    ENGLISH_BETA_LEGAL_SUMMARY_URL,
    ENGLISH_BETA_PRIVACY_URL,
    ENGLISH_BETA_SCTA_URL,
    ENGLISH_BETA_TERMS_URL,
    isEnglishBetaSignupSource,
    isEnglishOperatorMode,
} from "../utils/operatorMode";
import { handleStripeNextAction } from '../utils/stripeNextAction';

const API_URL = ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? (process.env.REACT_APP_API_URL || "http://localhost:3173")
    : window.location.origin;

const formatPlanAmount = (priceInfo, { monthlyEquivalent = false } = {}) => {
    if (!priceInfo) return "---";
    const rawAmount = Number(monthlyEquivalent ? priceInfo.monthly_equivalent : priceInfo.amount);
    if (!Number.isFinite(rawAmount)) return "---";

    if (String(priceInfo.currency || '').toLowerCase() === 'usd') {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
        }).format(rawAmount / 100);
    }

    return `¥${rawAmount.toLocaleString('ja-JP')}`;
};

const PLAN_PERIOD_MONTHS = {
    '1_month': 1,
    '6_months': 6,
    '12_months': 12,
};

const LP_PERIOD_OPTIONS = [
    { key: '1_month', label: '1ヶ月契約' },
    { key: '6_months', label: '半年契約' },
    { key: '12_months', label: '1年契約' },
];

const getPeriodMonths = (period) => PLAN_PERIOD_MONTHS[period] || 1;

// --- ▼▼▼ UI Components ▼▼▼ ---

/**
 * 期間選択ボタンのコンポーネント
 */
const PeriodSelector = ({ selectedPeriod, setSelectedPeriod, isEnglishBeta = false }) => {
    const periods = isEnglishBeta ? [
        { key: '12_months', label: 'Annual', discount: 'Billed annually' },
        { key: '1_month', label: 'Monthly' },
    ] : [
        { key: '12_months', label: '1年契約', discount: '2ヶ月分お得' },
        { key: '6_months', label: '半年契約', discount: '半月分お得' },
        { key: '1_month', label: '1ヶ月契約' },
    ];

    return (
        <div className="flex justify-center mb-8 sm:mb-10">
            <div className="w-full bg-slate-200 p-1 rounded-2xl sm:rounded-full flex items-center gap-1 flex-wrap justify-center sm:w-auto">
                {periods.map(period => (
                    <button
                        key={period.key}
                        onClick={() => setSelectedPeriod(period.key)}
                        className={`relative my-1 flex-1 rounded-full px-3 py-2 text-sm font-bold transition-colors duration-300 sm:flex-none sm:px-6 sm:text-base ${
                            selectedPeriod === period.key
                                ? 'bg-white text-indigo-600 shadow-md'
                                : 'text-slate-600 hover:bg-slate-300/60'
                        }`}
                    >
                        {period.label}
                        {period.discount && (
                            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                {period.discount}
                            </span>
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
};

/**
 * 料金プランカードのコンポーネント
 */
const PlanCard = ({ plan, tier, period, onSelect, isLoading, isSelected, isPopular, isEnglishBeta = false, isReturningOperator = false }) => {
    const isFreePlan = tier === 'free';
    const currentPriceInfo = plan.prices?.find(p => p.period === period);
    const monthlyPriceInfo = plan.prices?.find(p => p.period === '1_month');
    const displayPriceInfo = currentPriceInfo || monthlyPriceInfo;
    const isDisabledForPeriod = isFreePlan && period !== '1_month';
    const englishPeriodSuffix = displayPriceInfo?.period === '12_months' ? '/year' : '/mo';

    const features = isEnglishBeta ? {
        starter: ["Governed outreach execution", "Japanese company database access", "Standard execution capacity", "Email support"],
        standard: ["Governed outreach execution", "Japanese company database access", "Expanded execution capacity", "Dedicated support"],
        premium: ["Governed outreach execution", "Japanese company database access", "Reserved execution capacity", "Priority support"],
    } : {
        free: ["フォーム営業実行：月500件まで", "リストダウンロード：月100件まで", "送信パワー：基本", "サポートなし"],
        starter: ["フォーム営業実行：プラン範囲内", "企業リスト利用：プラン範囲内", "送信パワー：標準 (フリーの6倍)", "メールサポート"],
        standard: ["フォーム営業実行：プラン範囲内", "企業リスト利用：プラン範囲内", "送信パワー：高速 (フリーの30倍)", "専任サポート（月1回MTG）"],
        premium: ["フォーム営業実行：プラン範囲内", "企業リスト利用：プラン範囲内", "送信パワー：超高速 (フリーの60倍)", "LP無料で制作"],
    };
    const planFeatures = features[tier] || [];

    return (
        <div className={`bg-white rounded-2xl p-5 sm:p-8 shadow-lg border flex flex-col relative h-full transition-all duration-300 ${isPopular ? 'border-2 border-indigo-600' : 'border-slate-200'} ${isDisabledForPeriod ? 'opacity-50 bg-slate-50' : ''}`}>
            {isPopular && <span className="bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-full absolute top-0 -translate-y-1/2 mx-auto left-0 right-0 w-max text-center">{isEnglishBeta ? "Recommended" : "人気No.1"}</span>}
            <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
            <p className="mt-2 text-slate-600 flex-grow text-sm">{plan.description}</p>
            
            <div className="mt-6">
                {isFreePlan ? (
                    <span className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900">{isEnglishBeta ? "No free tier" : "¥0"}</span>
                ) : (
                    <>
                        <span className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900">
                            {isEnglishBeta
                                ? formatPlanAmount(displayPriceInfo)
                                : `¥${displayPriceInfo ? (displayPriceInfo.monthly_equivalent).toLocaleString() : '---'}`}
                        </span>
                        <span className="text-lg font-medium text-slate-500">{isEnglishBeta ? englishPeriodSuffix : "/月"}</span>
                    </>
                )}
            </div>

            {isEnglishBeta && !isFreePlan && displayPriceInfo?.period === '12_months' && (
                <p className="mt-2 text-xs font-medium text-slate-500">
                    {formatPlanAmount(displayPriceInfo, { monthlyEquivalent: true })}/mo equivalent. {isReturningOperator ? "Returning account: no additional operator trial is expected." : "3-day operator trial."}
                </p>
            )}
            {isEnglishBeta && !isFreePlan && displayPriceInfo?.period === '1_month' && (
                <p className="mt-2 text-xs font-medium text-slate-500">
                    {isReturningOperator ? "Returning account: no additional operator trial is expected." : "3-day operator trial."} No free tier.
                </p>
            )}

            <ul className="mt-6 sm:mt-8 space-y-3 sm:space-y-4 text-slate-600 flex-grow">
                {planFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start">
                        <span className="text-indigo-500 mr-2 mt-1">✓</span>
                        <span className="text-sm">{feature}</span>
                    </li>
                ))}
            </ul>

            <button
                onClick={() => !isDisabledForPeriod && onSelect(displayPriceInfo?.id, tier)}
                disabled={isLoading || isDisabledForPeriod || !displayPriceInfo}
                className={`mt-8 sm:mt-10 block w-full text-center font-bold py-3 px-4 sm:px-6 rounded-lg transition-colors duration-200 disabled:bg-slate-300 disabled:cursor-not-allowed ${
                    isFreePlan 
                    ? 'bg-slate-700 text-white hover:bg-slate-800'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
            >
                {isDisabledForPeriod
                    ? (isEnglishBeta ? 'Monthly only' : '長期プランなし')
                        : (isLoading && isSelected)
                        ? (isEnglishBeta ? 'Opening checkout...' : '処理中...')
                        : isFreePlan
                            ? (isEnglishBeta ? 'No free tier' : '無料で始める')
                            : (isEnglishBeta ? (isReturningOperator ? 'Resume with this plan' : 'Select this plan') : 'このプランで始める')}
            </button>
        </div>
    );
};

const EnglishBetaLegalNotice = ({ compact = false, returningOperator = false }) => (
    <div className={`${compact ? "mt-4 text-[11px]" : "mt-10 text-xs"} text-center leading-6 text-slate-500`}>
        <p>
            {returningOperator
                ? "Returning canceled accounts resume with the selected paid plan. No additional operator trial is expected."
                : "Includes a 3-day operator trial. A payment method is required. Unless canceled before the trial ends, the selected plan starts automatically."}
        </p>
        <p>
            USD prices are tax-exclusive. Tax collection is not enabled for the initial English Operator Beta unless otherwise required.
            English Operator Beta has no free tier and no 6-month plan.
        </p>
        <p>
            By continuing, you agree to the <a href={ENGLISH_BETA_TERMS_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-600">English Terms of Service</a>, <a href={ENGLISH_BETA_PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-600">English Privacy Policy</a>, and <a href={ENGLISH_BETA_SCTA_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-indigo-600">English commercial disclosure summary</a>. The Japanese documents are authoritative. <a href={ENGLISH_BETA_LEGAL_SUMMARY_URL} target="_blank" rel="noopener noreferrer" className="font-bold underline hover:text-indigo-600">Read the legal overview</a>.
        </p>
    </div>
);

// --- ▼▼▼ Main Page Component ▼▼▼ ---


// ===== 2026_09カタログ: 組み立て式プラン選択 =====
// available-plansが2026_09(plans.baseあり)を返すと自動でこのUIに切り替わる。
// 流れ: 期間を選ぶ → オプションにチェック(数量) → 合計とトライアル後請求を確認 → Stripeへ。
const COMPOSER_2026_PERIODS = [
    { key: '1_month', label: '月契約', badge: null },
    { key: '6_months', label: '半年一括', badge: '1ヶ月分無料' },
    { key: '12_months', label: '年間一括', badge: '4ヶ月分無料' },
];

const composerYen = (value) => `${Number(value || 0).toLocaleString('ja-JP')}円`;

// いまの契約内容を組み立て画面の初期値へ復元する (2026-08-19)。
// 確定時にサーバーは送られた items[] で明細を全置換するため、まっさらな
// 状態から始めると「オプションを1つ足すつもり」で既存のオプションと契約期間が
// 消える。復元できたかどうかを resolved で返し、復元できなかったときは
// 画面が黙って進まないようにする。
const resolveCurrentComposerSelection = (plans, currentSubscription) => {
    const fallback = { period: '12_months', jobSlotQty: 0, mailBlockQty: 0, supportTier: null, resolved: false };
    if (!currentSubscription || !plans?.base?.prices?.length) return fallback;
    // 有料の組み立て契約でなければ復元するものが無い (新規申込と同じ扱い)。
    const planId = String(currentSubscription.plan_id_string || '');
    if (!currentSubscription.stripe_subscription_id || planId === 'free' || planId === 'trial') return fallback;

    // 契約期間は base の Stripe 価格IDから引く (billing_period は価格側の正本)。
    const basePriceId = String(currentSubscription.stripe_price_id || '');
    const matchedBasePrice = basePriceId
        ? plans.base.prices.find((price) => String(price.id) === basePriceId)
        : null;
    if (!matchedBasePrice?.period) return fallback;

    const toQuantity = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
    };
    const storedSupport = String(currentSubscription.support_tier || '');
    return {
        period: matchedBasePrice.period,
        jobSlotQty: toQuantity(currentSubscription.additional_job_slots),
        mailBlockQty: toQuantity(currentSubscription.mail_block_quantity),
        supportTier: storedSupport === 'support_pro' || storedSupport === 'support_lite' ? storedSupport : null,
        resolved: true,
    };
};

const Composer2026 = ({ plans, currentSubscription, onSubmit, isBusy }) => {
    const initialSelection = useMemo(
        () => resolveCurrentComposerSelection(plans, currentSubscription),
        [plans, currentSubscription],
    );
    const [period, setPeriod] = useState(initialSelection.period);
    const [jobSlotQty, setJobSlotQty] = useState(initialSelection.jobSlotQty);
    const [mailBlockQty, setMailBlockQty] = useState(initialSelection.mailBlockQty);
    const [supportTier, setSupportTier] = useState(initialSelection.supportTier);
    // plans / subscription は非同期で届くので、届いてから一度だけ反映する
    // (ユーザーが触ったあとに上書きしないよう、初回のみ)。
    const prefillAppliedRef = useRef(false);
    useEffect(() => {
        if (prefillAppliedRef.current || !initialSelection.resolved) return;
        prefillAppliedRef.current = true;
        setPeriod(initialSelection.period);
        setJobSlotQty(initialSelection.jobSlotQty);
        setMailBlockQty(initialSelection.mailBlockQty);
        setSupportTier(initialSelection.supportTier);
    }, [initialSelection]);
    // 既存契約があるのに内容を復元できなかったとき。この状態で確定すると
    // いまのオプションと契約期間が入力内容で置き換わるので、必ず伝える。
    const hasExistingPaidContract = Boolean(
        currentSubscription?.stripe_subscription_id
        && !['free', 'trial'].includes(String(currentSubscription?.plan_id_string || ''))
    );
    const prefillFailed = hasExistingPaidContract && !initialSelection.resolved;

    const priceFor = useCallback((tier) => (
        plans[tier]?.prices?.find(p => p.period === period) || null
    ), [plans, period]);

    const months = getPeriodMonths(period);
    const quantities = {
        base: 1,
        job_slot_v2: jobSlotQty,
        mail_block_50k: mailBlockQty,
        support_lite: supportTier === 'support_lite' ? 1 : 0,
        support_pro: supportTier === 'support_pro' ? 1 : 0,
    };
    const totalAmount = Object.entries(quantities).reduce((sum, [tier, quantity]) => {
        if (quantity <= 0) return sum;
        const price = priceFor(tier);
        return sum + (price ? Number(price.amount) * quantity : 0);
    }, 0);
    const monthlyEquivalent = Math.floor(totalAmount / months);
    const basePrice = priceFor('base');
    const subscriptionStatus = String(currentSubscription?.status || '').toLowerCase();
    const hasMutableSubscription = Boolean(
        currentSubscription?.stripe_subscription_id
        && !['canceled', 'cancelled', 'incomplete_expired'].includes(subscriptionStatus)
        && currentSubscription?.plan_id_string !== 'free'
    );
    const missingPrice = Object.entries(quantities).some(([tier, quantity]) => quantity > 0 && !priceFor(tier));

    const stepButton = "h-9 w-9 rounded-full border border-slate-300 font-black text-slate-600 hover:border-indigo-500 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed";

    const handleSubmit = () => {
        if (!basePrice || missingPrice || isBusy) return;
        const items = Object.entries(quantities)
            .filter(([, quantity]) => quantity > 0)
            .map(([tier, quantity]) => ({ stripePriceId: priceFor(tier).id, quantity }));
        onSubmit(basePrice.id, items);
    };

    const quantityRow = (label, note, quantity, setQuantity, unitPrice) => (
        <div className="flex items-center justify-between gap-3 px-5 py-4">
            <div className="min-w-0">
                <p className="font-bold text-slate-800">{label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{note}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs font-bold text-slate-500 hidden sm:block">
                    {unitPrice ? `+${composerYen(unitPrice.amount)}/${months === 1 ? '月' : `${months}ヶ月`}` : '—'}
                </span>
                <button type="button" className={stepButton} onClick={() => setQuantity(Math.max(0, quantity - 1))} disabled={quantity <= 0}>−</button>
                <span className="w-6 text-center font-black text-slate-900">{quantity}</span>
                <button type="button" className={stepButton} onClick={() => setQuantity(Math.min(99, quantity + 1))} disabled={!unitPrice}>＋</button>
            </div>
        </div>
    );

    return (
        <div className="max-w-2xl mx-auto">
            <div className="text-center max-w-3xl mx-auto">
                <h1 className="text-2xl sm:text-4xl font-bold text-slate-900">営業OS</h1>
                <p className="mt-3 text-base sm:text-lg text-slate-600 font-medium">
                    プランはひとつ。期間を選んで、必要ならオプションを足すだけ。
                </p>
            </div>

            {/* いまの契約内容を復元できたか (2026-08-19)。確定すると明細は
                ここの内容で全部置き換わるので、その事実を先に伝える。 */}
            {initialSelection.resolved && (
                <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    いまのご契約内容を読み込みました。ここで確定すると、<strong className="font-bold">表示されている内容がそのまま新しいご契約になります</strong>。オプションを外すと、その分は解約になります。
                </div>
            )}
            {prefillFailed && (
                <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert">
                    <strong className="font-bold block">いまのご契約内容を読み込めませんでした</strong>
                    このまま確定すると、いま付いているオプションと契約期間が、この画面で選んだ内容に置き換わります。現在のご契約が分からない場合は、確定せずにお問い合わせください。
                </div>
            )}

            {/* 期間 */}
            <div className="mt-10 grid grid-cols-3 gap-3">
                {COMPOSER_2026_PERIODS.map(option => {
                    const optionPrice = plans.base?.prices?.find(p => p.period === option.key);
                    const isSelected = period === option.key;
                    return (
                        <button
                            key={option.key}
                            type="button"
                            onClick={() => optionPrice && setPeriod(option.key)}
                            disabled={!optionPrice}
                            className={`rounded-xl border p-3 text-center transition-all disabled:opacity-40 ${
                                isSelected ? 'border-indigo-600 bg-indigo-50 shadow-sm' : 'border-slate-200 bg-white hover:border-indigo-200'
                            }`}
                        >
                            <span className={`block text-sm font-black ${isSelected ? 'text-indigo-700' : 'text-slate-900'}`}>{option.label}</span>
                            {optionPrice && (
                                <span className="mt-1 block text-xs font-bold text-slate-500">
                                    {composerYen(optionPrice.monthly_equivalent)}/月
                                </span>
                            )}
                            {option.badge && (
                                <span className="mt-1 inline-flex rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-black text-red-600">{option.badge}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* base */}
            <div className="mt-6 rounded-2xl border-2 border-indigo-600 bg-white shadow-sm px-5 py-4 flex items-center justify-between">
                <div>
                    <p className="font-black text-slate-900">営業OS（全機能）</p>
                    <p className="text-xs text-slate-500 mt-0.5">基本送信処理枠×1・マーケティングメール月20,000通を含む</p>
                </div>
                <div className="text-right shrink-0">
                    <p className="font-black text-slate-900">{basePrice ? `${composerYen(basePrice.monthly_equivalent)}/月` : '—'}</p>
                    {months > 1 && basePrice && (
                        <p className="text-[11px] font-bold text-slate-400">一括 {composerYen(basePrice.amount)}</p>
                    )}
                </div>
            </div>

            {/* オプション */}
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
                {quantityRow(
                    '追加送信処理枠',
                    '送信の同時実行数を増やして速く回す（送信件数の上限ではありません）',
                    jobSlotQty, setJobSlotQty, priceFor('job_slot_v2'),
                )}
                {quantityRow(
                    'メルマガ増枠 +50,000通/月',
                    '月間マーケティングメール枠を50,000通ずつ追加',
                    mailBlockQty, setMailBlockQty, priceFor('mail_block_50k'),
                )}
                <div className="px-5 py-4">
                    <p className="font-bold text-slate-800">サポートプラン</p>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                        {[
                            { key: null, label: 'なし', sub: null },
                            { key: 'support_lite', label: 'Lite', sub: priceFor('support_lite') },
                            { key: 'support_pro', label: 'Pro', sub: priceFor('support_pro') },
                        ].map(option => (
                            <button
                                key={String(option.key)}
                                type="button"
                                onClick={() => setSupportTier(option.key)}
                                disabled={option.key !== null && !option.sub}
                                className={`rounded-xl border p-2.5 text-center transition-all disabled:opacity-40 ${
                                    supportTier === option.key ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200 bg-white hover:border-indigo-200'
                                }`}
                            >
                                <span className="block text-sm font-black text-slate-900">{option.label}</span>
                                {option.sub && (
                                    <span className="block text-[11px] font-bold text-slate-500">+{composerYen(option.sub.monthly_equivalent)}/月</span>
                                )}
                            </button>
                        ))}
                    </div>
                    <p className="mt-2 text-[11px] text-slate-400">Lite=個別サポート+運用コンサル月1回 / Pro=Lite+コンサル月2回+LP作成支援</p>
                </div>
            </div>

            {/* 合計とCTA */}
            <div className="mt-6 rounded-2xl bg-slate-900 text-white px-6 py-5">
                <div className="flex items-baseline justify-between">
                    <span className="text-sm font-bold text-slate-300">合計</span>
                    <span>
                        <span className="text-3xl font-black">{composerYen(monthlyEquivalent)}</span>
                        <span className="text-sm font-bold text-slate-300">/月</span>
                    </span>
                </div>
                {months > 1 && (
                    <p className="text-right text-xs font-bold text-slate-400 mt-1">一括 {composerYen(totalAmount)}（{months}ヶ月分）</p>
                )}
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isBusy || !basePrice || missingPrice}
                    className="mt-4 w-full bg-indigo-500 hover:bg-indigo-400 text-white font-black py-3.5 rounded-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {isBusy ? '処理中...' : (hasMutableSubscription ? 'このプラン構成に変更する' : '3日間無料で試す')}
                </button>
                <p className="mt-3 text-[11px] leading-relaxed text-slate-400 text-center">
                    {hasMutableSubscription
                        ? '切り替え時に、現在の契約の未使用分を差し引いて精算されます。'
                        : `無料トライアルにはクレジットカードの登録が必要です。3日以内の解約で請求は発生しません。トライアル終了後、${months === 1 ? `月額 ${composerYen(totalAmount)}` : `${months}ヶ月分一括 ${composerYen(totalAmount)}`}の請求が始まります。`}
                </p>
            </div>
        </div>
    );
};

const PlanSelectionPage = () => {
    const isEnglishBetaSurface = isEnglishOperatorMode();
    const [isLoading, setIsLoading] = useState(true);
    const [isCreatingSession, setIsCreatingSession] = useState(false);
    const [selectedPlanPriceId, setSelectedPlanPriceId] = useState(null);
    const [error, setError] = useState('');
    const [plans, setPlans] = useState({});
    const [selectedPeriod, setSelectedPeriod] = useState('12_months');
    const [lpSelectedPeriod, setLpSelectedPeriod] = useState('1_month');
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [signupSource, setSignupSource] = useState(null);
    const [errorRequiresLogin, setErrorRequiresLogin] = useState(false);

    const user = useMemo(() => {
        const storedUser = localStorage.getItem("userInfo");
        try {
            return storedUser ? JSON.parse(storedUser) : null;
        } catch (e) {
            return null;
        }
    }, []);
    const isEnglishBeta = isEnglishBetaSurface || isEnglishBetaSignupSource(user?.signup_source || signupSource);

    const fetchInitialData = useCallback(async () => {
        // ★ 修正箇所: localStorage.getItem("token") から getToken() に変更
        // auth.js の getToken() は正しいキー名 'authToken' を参照します
        const token = getToken(); 
        
        if (!token) {
            setError(isEnglishBeta ? "Authentication token not found. Please log in again." : "認証トークンが見つかりません。再度ログインしてください。");
            setErrorRequiresLogin(true);
            setIsLoading(false);
            return;
        }

        const tempUser = user || JSON.parse(localStorage.getItem("userInfo"));
        if (!tempUser || !tempUser.client_id) {
            setError(isEnglishBeta ? "User profile not found. Please log in again." : "ユーザー情報が見つかりません。再ログインをお試しください。");
            setErrorRequiresLogin(true);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setErrorRequiresLogin(false);
        try {
            const plansUrl = `${API_URL}/api/available-plans?clientId=${tempUser.client_id}${isEnglishBeta ? '&surface=english_beta' : ''}`;
            const [plansRes, statusRes] = await Promise.all([
                fetch(plansUrl, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_URL}/api/subscription-status?userId=${tempUser.id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);
            
            const plansData = await plansRes.json();
            const statusData = await statusRes.json();

            if (plansRes.status === 401 || statusRes.status === 401) {
                const authError = new Error(isEnglishBeta ? "Your session expired. Please log in again." : "セッションが切れました。再度ログインしてください。");
                authError.requiresLogin = true;
                throw authError;
            }

            if (plansData.success) setPlans(plansData.plans || {});
            if (statusData.success) {
                console.log("DEBUG: Plan Status Data", statusData);
                setCurrentSubscription(statusData.subscription || null);
                setSignupSource(statusData.signup_source);
                if (isEnglishBetaSignupSource(statusData.signup_source)) {
                    try {
                        const storedUser = JSON.parse(localStorage.getItem("userInfo") || "null");
                        if (storedUser && !isEnglishBetaSignupSource(storedUser.signup_source)) {
                            localStorage.setItem("userInfo", JSON.stringify({
                                ...storedUser,
                                signup_source: statusData.signup_source,
                            }));
                        }
                        localStorage.setItem("operator_surface", "english_beta");
                    } catch (storageError) {
                        // Ignore storage errors in restricted browser contexts.
                    }
                }
            }
        } catch (err) {
            setError(err.message);
            setErrorRequiresLogin(Boolean(err.requiresLogin));
        } finally {
            setIsLoading(false);
        }
    }, [user, isEnglishBeta]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchInitialData();
        }, 100);
        return () => clearTimeout(timer);
    }, [fetchInitialData]);

    const handlePlanSelection = async (stripePriceId, planIdString, items = null) => {
        if (!stripePriceId) return;
        const currentUser = user || JSON.parse(localStorage.getItem("userInfo"));
        setIsCreatingSession(true);
        setSelectedPlanPriceId(stripePriceId);
        setError('');
        setErrorRequiresLogin(false);
        try {
            const subscriptionStatus = String(currentSubscription?.status || '').toLowerCase();
            const terminalStatuses = new Set(['canceled', 'cancelled', 'incomplete_expired']);
            const hasMutableStripeSubscription = Boolean(
                currentSubscription?.stripe_subscription_id
                && !terminalStatuses.has(subscriptionStatus)
            );

            if (hasMutableStripeSubscription) {
                if (planIdString === 'free') {
                    window.location.href = currentSubscription?.plan_id_string === 'free'
                        ? '/app/account'
                        : '/app/account/free-plan-survey';
                    return;
                }
                if (!items && String(currentSubscription.current_stripe_price_id || '') === String(stripePriceId)) {
                    throw new Error(isEnglishBeta ? 'This billing option is already active.' : 'この請求条件はすでに適用されています。');
                }

                const updateResponse = await fetch(`${API_URL}/api/subscriptions/update`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...getAuthHeader(),
                    },
                    body: JSON.stringify({
                        subscriptionId: currentSubscription.stripe_subscription_id,
                        newPriceId: stripePriceId,
                        ...(items ? { items } : {}),
                        surface: isEnglishBeta ? 'english_beta' : undefined,
                        operator_surface: isEnglishBeta ? 'english_beta' : undefined,
                    }),
                });
                const updateData = await updateResponse.json();
                if (updateData.requires_action && updateData.client_secret) {
                    await handleStripeNextAction({
                        clientSecret: updateData.client_secret,
                        publishableKey: updateData.publishable_key,
                        isEnglishBeta,
                    });
                    window.location.href = '/app/account?payment_action=completed';
                    return;
                }
                if (!updateResponse.ok || !updateData.success) {
                    const updateError = new Error(updateData.error || (isEnglishBeta ? 'Could not change the plan.' : 'プラン変更に失敗しました。'));
                    updateError.requiresLogin = updateResponse.status === 401;
                    throw updateError;
                }
                window.location.href = '/app/account?plan_change=updated';
                return;
            }

            const response = await fetch(`${API_URL}/api/create-checkout-session`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    ...getAuthHeader()
                },
                body: JSON.stringify({
                    userId: currentUser.id,
                    stripePriceId: stripePriceId,
                    planIdString: planIdString,
                    ...(items ? { items } : {}),
                    surface: isEnglishBeta ? 'english_beta' : undefined,
                    operator_surface: isEnglishBeta ? 'english_beta' : undefined,
                }),
            });
            const data = await response.json();
            if (!response.ok) {
                const checkoutError = new Error(data.error || (isEnglishBeta ? 'Could not create the checkout session.' : '決済ページの作成に失敗しました。'));
                checkoutError.requiresLogin = response.status === 401;
                throw checkoutError;
            }
            window.location.href = data.url;
        } catch (err) {
            setError(err.message);
            setErrorRequiresLogin(Boolean(err.requiresLogin));
            setIsCreatingSession(false);
            setSelectedPlanPriceId(null);
        }
    };
    
    // LP経由かどうかの判定
    const isLpUser = useMemo(() => {
        const normalizedSignupSource = String(signupSource || '').trim().toLowerCase();
        const lpSourcePrefixes = ['meta_ads_lp', 'form_lp'];
        const sourceMatches = lpSourcePrefixes.some(prefix => (
            normalizedSignupSource === prefix ||
            normalizedSignupSource.startsWith(`${prefix}_`) ||
            normalizedSignupSource.startsWith(`inquiry_ref:${prefix}`)
        ));
        const noActivePaidPlan = !currentSubscription || 
                                 currentSubscription.plan_id_string === 'trial' || 
                                 currentSubscription.plan_id_string === 'free';
        
        console.log("DEBUG: isLpUser Check", { signupSource, plan: currentSubscription?.plan_id_string, isLpUser: sourceMatches && noActivePaidPlan });
        return sourceMatches && noActivePaidPlan;
    }, [signupSource, currentSubscription]);

    const lpTargetPlan = plans['starter'];
    const lpTargetPrices = useMemo(() => {
        const prices = lpTargetPlan?.prices || [];
        return prices
            .filter(price => LP_PERIOD_OPTIONS.some(option => option.key === price.period))
            .sort((a, b) => getPeriodMonths(a.period) - getPeriodMonths(b.period));
    }, [lpTargetPlan]);
    const lpMonthlyPrice = lpTargetPrices.find(p => p.period === '1_month');
    const lpTargetPrice = lpTargetPrices.find(p => p.period === lpSelectedPeriod) || lpMonthlyPrice || lpTargetPrices[0];
    const lpSelectedMonths = getPeriodMonths(lpTargetPrice?.period);
    const lpSavingsAmount = lpMonthlyPrice && lpSelectedMonths > 1
        ? Math.max(0, (Number(lpMonthlyPrice.amount || 0) * lpSelectedMonths) - Number(lpTargetPrice?.amount || 0))
        : 0;
    const normalizedSubscriptionStatus = String(currentSubscription?.status || '').toLowerCase();
    const isReturningEnglishBetaOperator = isEnglishBeta && normalizedSubscriptionStatus === 'canceled';
    const is2026Catalog = !isEnglishBeta && Boolean(plans?.base);
    const planDisplayOrder = isEnglishBeta ? ['starter'] : ['free', 'starter', 'standard', 'premium'];
    const planGridClassName = isEnglishBeta
        ? "grid grid-cols-1 gap-5 sm:gap-8 mt-8 max-w-xl mx-auto items-stretch"
        : "grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8 mt-8 max-w-5xl mx-auto items-stretch";

    return (
        <div className="bg-slate-50 min-h-screen py-8 sm:py-20">
            <div className="container mx-auto px-3 sm:px-6">
                
                {error && (
                    <div role="alert" aria-live="assertive" className="max-w-3xl mx-auto mt-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-8">
                        {error}
                        {errorRequiresLogin && (
                            <div className="mt-2 text-sm">
                                <button onClick={() => window.location.href='/app/login'} className="underline font-bold">{isEnglishBeta ? "Back to login" : "ログイン画面に戻る"}</button>
                            </div>
                        )}
                    </div>
                )}
                
                {isLoading ? (
                    <div className="text-center mt-12 text-slate-600 font-bold animate-pulse">
                        {isEnglishBeta ? "Loading plan options..." : "プラン情報を確認しています..."}
                    </div>
                ) : (
                    <>
                        {is2026Catalog ? (
                            <Composer2026
                                plans={plans}
                                currentSubscription={currentSubscription}
                                isBusy={isCreatingSession}
                                onSubmit={(basePriceId, items) => handlePlanSelection(basePriceId, 'base', items)}
                            />
                        ) : isLpUser && lpTargetPlan && lpTargetPrice ? (
                            <div className="max-w-xl mx-auto">
                                <div className="text-center mb-10">
                                    <span className="bg-indigo-100 text-indigo-700 text-sm font-bold px-4 py-2 rounded-full inline-block mb-4">
                                        {isEnglishBeta ? "Registration received" : "ご登録ありがとうございます！"}
                                    </span>
                                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-relaxed">
                                        {isEnglishBeta ? (
                                            <>
                                                Confirm your English Operator plan for<br/>
                                                <span className="text-indigo-600">Japan GTM execution</span>
                                            </>
                                        ) : (
                                            <>
                                                Sales Breakerの威力を<br/>
                                                <span className="text-indigo-600">3日間無料</span>で体験してください
                                            </>
                                        )}
                                    </h1>
                                </div>

                                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-indigo-600 relative">
                                    <div className="bg-indigo-600 text-white text-center py-3 font-bold text-lg">
                                        {isEnglishBeta ? "Recommended beta plan" : "👇 あなたに適用される限定プラン"}
                                    </div>
                                    
                                    <div className="p-5 sm:p-8">
                                        <div className="text-center mb-6">
                                            <h3 className="text-2xl font-bold text-slate-900">{lpTargetPlan.name}</h3>
                                            <p className="text-slate-500 text-sm mt-1">{lpTargetPlan.description}</p>
                                        </div>

                                        <div className="bg-slate-50 rounded-xl p-5 sm:p-6 mb-8 text-center border border-slate-200">
                                            <p className="text-sm font-bold text-slate-500 mb-2">{isEnglishBeta ? "3-day operator trial" : "無料トライアル期間中"}</p>
                                            <div className="flex items-center justify-center gap-2">
                                                <span className="text-4xl sm:text-5xl font-black text-slate-900">{isEnglishBeta ? formatPlanAmount(lpTargetPrice) : "¥0"}</span>
                                                {isEnglishBeta && <span className="text-slate-500 font-bold">/mo</span>}
                                            </div>
                                            <p className="text-xs text-red-500 font-bold mt-3">
                                                {isEnglishBeta ? "Cancel during the trial period to avoid charges." : "※期間内に解約すれば料金は発生しません"}
                                            </p>
                                        </div>

                                        {!isEnglishBeta && (
                                            <div className="mb-8">
                                                <p className="mb-3 text-sm font-bold text-slate-700">無料期間後の契約期間を選択</p>
                                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                                    {LP_PERIOD_OPTIONS.map(option => {
                                                        const price = lpTargetPrices.find(p => p.period === option.key);
                                                        const months = getPeriodMonths(option.key);
                                                        const isSelected = lpTargetPrice?.period === option.key;
                                                        const savings = price && lpMonthlyPrice && months > 1
                                                            ? Math.max(0, (Number(lpMonthlyPrice.amount || 0) * months) - Number(price.amount || 0))
                                                            : 0;

                                                        return (
                                                            <button
                                                                key={option.key}
                                                                type="button"
                                                                onClick={() => price && setLpSelectedPeriod(option.key)}
                                                                disabled={!price}
                                                                className={`rounded-xl border p-3 text-left transition-all disabled:cursor-not-allowed disabled:opacity-40 ${
                                                                    isSelected
                                                                        ? 'border-indigo-600 bg-indigo-50 shadow-sm'
                                                                        : 'border-slate-200 bg-white hover:border-indigo-200 hover:bg-slate-50'
                                                                }`}
                                                            >
                                                                <span className={`block text-sm font-black ${isSelected ? 'text-indigo-700' : 'text-slate-900'}`}>{option.label}</span>
                                                                <span className="mt-1 block text-xs font-bold text-slate-500">
                                                                    実質 {formatPlanAmount(price, { monthlyEquivalent: true })}/月
                                                                </span>
                                                                <span className="mt-1 block text-[11px] text-slate-500">
                                                                    {months === 1
                                                                        ? `${formatPlanAmount(price)} / 月`
                                                                        : `${formatPlanAmount(price)} / ${months}ヶ月分`}
                                                                </span>
                                                                {savings > 0 && (
                                                                    <span className="mt-2 inline-flex rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-black text-red-600">
                                                                        月払いより{savings.toLocaleString('ja-JP')}円お得
                                                                    </span>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                                {lpTargetPrice && (
                                                    <div className="mt-4 rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-slate-700">
                                                        <p className="font-bold">
                                                            無料期間終了後：
                                                            {lpSelectedMonths === 1
                                                                ? `${formatPlanAmount(lpTargetPrice)} / 月`
                                                                : `${formatPlanAmount(lpTargetPrice)} / ${lpSelectedMonths}ヶ月分`}
                                                        </p>
                                                        {lpSavingsAmount > 0 && (
                                                            <p className="mt-1 text-xs font-bold text-red-600">
                                                                月払いより{lpSavingsAmount.toLocaleString('ja-JP')}円お得です。
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="space-y-4 mb-8">
                                            {(isEnglishBeta ? [
                                                "Governed outreach execution",
                                                "Japanese company database access",
                                                "Email support"
                                            ] : [
                                                "フォーム営業実行：プラン範囲内",
                                                "企業リスト利用：プラン範囲内",
                                                "メールサポート付き"
                                            ]).map((text, i) => (
                                                <div key={i} className="flex items-center">
                                                    <span className="bg-indigo-100 text-indigo-600 p-1 rounded-full mr-3 flex-shrink-0">
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                                                    </span>
                                                    <span className="text-slate-700 font-medium text-sm">{text}</span>
                                                </div>
                                            ))}
                                        </div>

                                        <button
                                            onClick={() => handlePlanSelection(lpTargetPrice.id, 'starter')}
                                            disabled={isCreatingSession}
                                            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-base sm:text-lg py-4 px-4 sm:px-6 rounded-xl shadow-lg transform transition hover:-translate-y-1 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
                                        >
                                            {isCreatingSession ? (isEnglishBeta ? 'Opening checkout...' : '処理中...') : (isEnglishBeta ? 'Continue to checkout' : 'クレジットカードを登録して無料で始める')}
                                        </button>
                                        
                                        <p className="text-[10px] text-center text-slate-400 mt-4 leading-relaxed">
                                            {isEnglishBeta ? (
                                                "Paid-only limited operator beta. No free tier."
                                            ) : (
                                                <>
                                                    ※無料期間終了後（4日目）から選択プランに応じて課金されます。<br/>
                                                    ※いつでもマイページから解約・プラン変更が可能です。
                                                </>
                                            )}
                                        </p>
                                        {isEnglishBeta && <EnglishBetaLegalNotice compact returningOperator={isReturningEnglishBetaOperator} />}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="text-center max-w-3xl mx-auto">
                                    <h1 className="text-2xl sm:text-4xl font-bold text-slate-900">
                                        {isEnglishBeta ? (isReturningEnglishBetaOperator ? "Welcome back" : "Choose your Starter plan") : "プランをお選びください"}
                                    </h1>
                                    <p className="mt-4 text-base sm:text-lg text-slate-600 font-medium italic">
                                        {isEnglishBeta
                                            ? (isReturningEnglishBetaOperator
                                                ? "Your English Operator Beta account is inactive. Choose monthly or annual Starter to resume."
                                                : "Paid-only limited operator beta. Starter is available monthly or annually.")
                                            : "~ 営業を、もっとスマートに。 ~"}
                                    </p>
                                </div>

                                {isReturningEnglishBetaOperator && (
                                    <div className="mx-auto mt-8 max-w-3xl rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm leading-6 text-amber-900">
                                        This account has already had a subscription history, so checkout should not show another 3-day operator trial. Select a paid plan to reactivate the English Operator Beta.
                                    </div>
                                )}

                                <div className="mt-12">
                                    <PeriodSelector selectedPeriod={selectedPeriod} setSelectedPeriod={setSelectedPeriod} isEnglishBeta={isEnglishBeta} />
                                </div>
                                
                                <div className={planGridClassName}>
                                    {planDisplayOrder
                                        .filter(tier => plans[tier])
                                        .map(tier => {
                                            const plan = plans[tier];
                                            const currentPriceInfo = plan.prices?.find(p => p.period === selectedPeriod) || plan.prices?.[0];
                                            return (
                                                <PlanCard
                                                    key={tier}
                                                    plan={plan}
                                                    tier={tier}
                                                    period={selectedPeriod}
                                                    onSelect={handlePlanSelection}
                                                    isLoading={isCreatingSession}
                                                    isSelected={selectedPlanPriceId === currentPriceInfo?.id}
                                                    isPopular={isEnglishBeta ? tier === 'starter' : tier === 'standard'}
                                                    isEnglishBeta={isEnglishBeta}
                                                    isReturningOperator={isReturningEnglishBetaOperator}
                                                />
                                            )
                                        })
                                    }
                                </div>
                                {isEnglishBeta && (
                                    <div className="mx-auto mt-8 max-w-xl rounded-xl border border-slate-200 bg-white px-5 py-4 text-center text-sm leading-6 text-slate-600">
                                        Need higher-volume execution? Standard and Premium capacity are handled through Help & Support during the English workspace rollout.
                                        <a href="/app/help?surface=english_beta" className="ml-1 font-bold text-indigo-600 underline hover:text-indigo-700">Open support</a>.
                                    </div>
                                )}
                                {isEnglishBeta && <EnglishBetaLegalNotice returningOperator={isReturningEnglishBetaOperator} />}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default PlanSelectionPage;
